/**
 * EmployeeImportExportPage
 * Dedicated page for bulk import/export operations
 * Supports 3-step import flow: Upload → Mapeamento → Resultado
 */

import React, { useState, useRef } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  FileDownload,
  FileUpload,
  CheckCircle,
  Error,
  Warning as WarningIcon,
  CloudDownload,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { useEmployeeImport, useEmployeeExport } from '../../hooks';

interface ImportErrorRow {
  line: number;
  error: string;
}

interface ImportWarningRow {
  line: number;
  warning: string;
}

const errorColumns: DataTableColumn<ImportErrorRow>[] = [
  {
    id: 'line',
    label: 'Linha',
    render: (row) => <Chip label={row.line} size="small" color="error" />,
  },
  { id: 'error', label: 'Erro', primary: true, render: (row) => row.error },
];

const warningColumns: DataTableColumn<ImportWarningRow>[] = [
  {
    id: 'line',
    label: 'Linha',
    render: (row) => <Chip label={row.line} size="small" color="warning" />,
  },
  { id: 'warning', label: 'Aviso', primary: true, render: (row) => row.warning },
];

const COLUMN_LABELS: Record<string, string> = {
  empresa_nome: 'Empresa',
  setor_nome: 'Setor',
  cargo_nome: 'Cargo',
  genero: 'Gênero',
  estado_civil: 'Estado Civil',
};

const IMPORT_STEPS = ['Upload', 'Mapeamento', 'Resultado'];

export const EmployeeImportExportPage: React.FC = () => {
  const {
    importing,
    previewing,
    importResult,
    preview,
    mappings,
    previewImport,
    confirmImport,
    setMappingValue,
    allRequiredMapped,
    downloadTemplate,
    clearResult,
    clearPreview,
  } = useEmployeeImport();
  const { exporting, exportEmployees } = useEmployeeExport();

  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    event.target.value = '';

    setFile(selectedFile);
    const previewResult = await previewImport(selectedFile);

    if (previewResult) {
      if (previewResult.unresolvedTerms.length > 0) {
        setActiveStep(1);
      } else {
        // No unresolved terms — import directly
        setActiveStep(2);
        await confirmImport(selectedFile);
      }
    }
  };

  const handleConfirmImport = async () => {
    if (!file) return;
    setActiveStep(2);
    await confirmImport(file);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    clearResult();
    clearPreview();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleExport = async () => {
    await exportEmployees(undefined, `funcionarios-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Funcionários', path: '/employees' },
        { label: 'Importação/Exportação' },
      ]}
      title="Importação e Exportação em Massa"
      subtitle="Gerencie funcionários em massa através de planilhas Excel"
    >
      <Grid container spacing={3}>
        {/* Export Section */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Card>
            <CardHeader
              avatar={<FileDownload />}
              title="Exportar Funcionários"
              subheader="Baixe todos os funcionários em Excel"
            />
            <Divider />
            <CardContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Como funciona</AlertTitle>
                O arquivo Excel conterá todos os funcionários cadastrados no sistema com todas as informações.
              </Alert>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dados Completos"
                    secondary="Todas as informações dos funcionários"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Formato Excel"
                    secondary="Arquivo .xlsx compatível com Excel e Google Sheets"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Filtros Aplicados"
                    secondary="Respeita os filtros ativos na listagem"
                  />
                </ListItem>
              </List>

              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<FileDownload />}
                onClick={handleExport}
                disabled={exporting}
                sx={{ mt: 2 }}
              >
                {exporting ? 'Exportando...' : 'Exportar Agora'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Import Section */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Card>
            <CardHeader
              avatar={<FileUpload />}
              title="Importar Funcionários"
              subheader="Cadastre vários funcionários de uma vez"
            />
            <Divider />
            <CardContent>
              <Stepper activeStep={activeStep} sx={{ mb: 3 }} alternativeLabel>
                {IMPORT_STEPS.map((label) => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>

              {/* Step 0: Upload */}
              {activeStep === 0 && (
                <>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>Atenção</AlertTitle>
                    Certifique-se de usar o template correto para evitar erros na importação.
                  </Alert>

                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<CloudDownload />}
                    onClick={downloadTemplate}
                    sx={{ mb: 2 }}
                  >
                    Baixar Template
                  </Button>

                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: file ? 'primary.main' : 'grey.400',
                      borderRadius: 1,
                      p: 3,
                      textAlign: 'center',
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1">
                      Clique para selecionar um arquivo
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Formatos aceitos: .xlsx, .xls (máx. 5MB)
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleImportFile}
                      style={{ display: 'none' }}
                    />
                  </Box>

                  {previewing && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Analisando arquivo...
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Step 1: Mapping */}
              {activeStep === 1 && preview && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Os termos abaixo não foram encontrados automaticamente. Selecione o valor correspondente para cada um. Campos obrigatórios (*) devem ser mapeados para prosseguir.
                  </Alert>

                  {preview.errors.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {preview.errors.length} linha(s) com erros de validação (serão ignoradas na importação).
                    </Alert>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {preview.totalRows} linha(s) encontrada(s), {preview.validRows} válida(s).
                  </Typography>

                  {preview.unresolvedTerms.map((term) => {
                    const options = preview.availableOptions[term.column] ?? [];
                    return (
                      <Box key={`${term.column}-${term.term}`} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          {COLUMN_LABELS[term.column] ?? term.column}{term.required ? ' *' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip label={term.term} size="small" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            ({term.count} ocorrência{term.count > 1 ? 's' : ''})
                          </Typography>
                        </Box>
                        <Autocomplete
                          size="small"
                          options={options}
                          getOptionLabel={(option) => option.label}
                          value={options.find(o => o.value === (mappings[term.column]?.[term.term] ?? '')) ?? null}
                          onChange={(_, newValue) => setMappingValue(term.column, term.term, newValue?.value ?? '')}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Selecione..." />
                          )}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                        />
                      </Box>
                    );
                  })}

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleReset}
                      disabled={importing}
                    >
                      Voltar
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleConfirmImport}
                      disabled={importing || !allRequiredMapped()}
                      startIcon={<FileUpload />}
                      fullWidth
                    >
                      {importing ? 'Importando...' : 'Confirmar Importação'}
                    </Button>
                  </Box>

                  {importing && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress />
                    </Box>
                  )}
                </>
              )}

              {/* Step 2: Result (shown inline if no unresolved terms) */}
              {activeStep === 2 && !importResult && importing && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Importando funcionários...
                  </Typography>
                </Box>
              )}

              {activeStep === 2 && importResult && (
                <Box>
                  <Button variant="outlined" onClick={handleReset} sx={{ mb: 2 }}>
                    Nova Importação
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Import Results */}
        {importResult && (
          <Grid size={12}>
            <Card>
              <CardHeader
                title="Resultado da Importação"
                action={
                  <Button onClick={handleReset}>Nova Importação</Button>
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 4
                    }}>
                    <Paper sx={{ p: 2, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircle color="success" />
                        <Box>
                          <Typography variant="h4" color="success.main">
                            {importResult.success}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Importados com Sucesso
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 4
                    }}>
                    <Paper sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <WarningIcon color="warning" />
                        <Box>
                          <Typography variant="h4" color="warning.main">
                            {importResult.warnings?.length ?? 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Avisos
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 4
                    }}>
                    <Paper sx={{ p: 2, bgcolor: 'error.50', borderLeft: '4px solid', borderColor: 'error.main' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Error color="error" />
                        <Box>
                          <Typography variant="h4" color="error.main">
                            {importResult.errors.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Erros Encontrados
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                {importResult.errors.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Detalhes dos Erros
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <DataTable
                        columns={errorColumns}
                        rows={importResult.errors}
                        getRowId={(row) => `${row.line}-${row.error}`}
                        emptyState={{
                          title: 'Nenhum erro encontrado',
                          description:
                            'Os erros vêm da validação de cada linha do arquivo importado.',
                        }}
                      />
                    </Box>
                  </>
                )}

                {(importResult.warnings?.length ?? 0) > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Avisos
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Avisos indicam ajustes automáticos feitos durante a importação. Os funcionários foram importados, mas verifique se os dados estão corretos.
                    </Alert>
                    <DataTable
                      columns={warningColumns}
                      rows={importResult.warnings!}
                      getRowId={(row) => `${row.line}-${row.warning}`}
                      emptyState={{
                        title: 'Nenhum aviso encontrado',
                        description:
                          'Os avisos apontam ajustes automáticos feitos nas linhas deste arquivo.',
                      }}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Instructions */}
        <Grid size={12}>
          <Card>
            <CardHeader title="Instruções e Requisitos" />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Campos Obrigatórios
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Nome Completo" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="CPF (somente números)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Data de Nascimento (DD/MM/AAAA)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Gênero (M, F ou O)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Nome da Mãe" />
                    </ListItem>
                  </List>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Formatos Aceitos
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Telefone: (XX) XXXXX-XXXX ou somente números" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="CEP: XXXXX-XXX ou somente números" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Data: DD/MM/AAAA" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Tamanho máximo: 5MB" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Formatos: .xlsx, .xls" />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>Dica</AlertTitle>
                CPFs duplicados serão ignorados automaticamente. Termos não reconhecidos para empresa,
                setor, cargo, gênero ou estado civil serão apresentados para mapeamento manual antes da importação.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageShell>
  );
};
